import { warmPing } from './api';

/**
 * 콜드스타트 가리기 — 목록 함수들을 **미리 깨워 두고, 일하는 동안 식지 않게** 한다.
 *
 * 왜 있는가 (2026-07-31 prod 실측)
 *   같은 함수를 연속 3회 부르면 **2.38초 / 0.17초 / 0.17초** 가 나온다. 느린 건 쿼리가 아니라
 *   콜드스타트다. 그런데 businweb 은 callable 이 **100개가 넘고** 하나가 곧 하나의 컨테이너라
 *   화면을 옮길 때마다 **그 화면 것만 식은 채로** 뜬다. 사용자에게는 "전반적으로 느리다"로 읽힌다.
 *
 * 왜 **반복**하는가 (2026-09-17 사용자 신고 "화면마다 오래 걸린다" · prod 실측)
 *   처음엔 로그인 직후 **한 번만** 깨웠다. 그런데 컨테이너는 **쓰지 않으면 약 15분 뒤 내려간다** —
 *   한 화면에서 20분 일하고 다른 메뉴로 가면 그 화면은 다시 식어 있다.
 *   실측: `listResultFinish` 식음 **2,638ms** → 웜 **191ms** · 로그인 직후 동시 워밍 20개는 **2.7~7.9초**.
 *   → 사람이 **실제로 쓰고 있는 동안만**(최근 15분 안에 조작 + 탭이 보임) 10분마다 다시 깨운다.
 *
 * 왜 `minInstances` 가 아닌가
 *   그건 유휴 인스턴스를 상시 띄워 두는 값이라 **아무도 안 쓰는 밤에도 과금**된다.
 *   100개 함수에 걸 수도 없고 몇 개만 걸면 나머지가 그대로 느리다. 여기 워밍은 공짜에 가깝다 —
 *   DB 를 안 읽는 호출(아래 WARM 주석)이고, 자리를 비우면 멈춘다.
 *
 * 규칙
 *   · **읽기 전용 함수만** 부른다. 쓰기 함수를 워밍에 넣으면 로그인만 해도 데이터가 바뀐다.
 *     예외: 첫 await 가 `requireUser` 인 쓰기 함수 — 워밍 표시(`__warm`)가 거기서 DB 전에 멈춘다(아래 WARM 끝 두 개).
 *   · **실패를 삼킨다.** 워밍은 화면에 아무것도 못 하게 만들면 안 된다 — 권한이 없어 막히든
 *     인자가 안 맞아 400 이 나든 컨테이너는 이미 떴고, 그게 목적이다.
 *   · **로딩 카드에 안 센다**(`warmPing` 은 로딩 카드를 거치지 않는다) — 사람이 기다리는 요청이 아니다.
 *   · 캐시를 거치지 않는다 — 캐시가 살아 있으면 컨테이너를 못 깨운다.
 */

/** 다시 깨우는 간격. 컨테이너가 내려가는 유휴 시간(약 15분)보다 짧아야 한다. */
const REWARM_MS = 10 * 60_000;
/** 이 시간 안에 조작이 없으면 자리를 비운 것으로 보고 깨우지 않는다. */
const IDLE_MS = 15 * 60_000;

let timer: ReturnType<typeof setInterval> | null = null;
let lastActive = Date.now();
const markActive = () => {
  lastActive = Date.now();
};
const ACTIVITY = ['pointerdown', 'keydown', 'wheel'] as const;

/** 하나라도 던져서 나머지가 안 도는 일이 없게. 워밍 실패는 조용히 버린다. */
function fire(run: () => Promise<unknown>): Promise<void> {
  return run().then(
    () => undefined,
    () => undefined,
  );
}

/**
 * 상단 메뉴가 첫 진입에 부르는 함수들. 순서는 의미 없다.
 *
 * 🔴 이름만 보낸다(`warmPing` — `{ __warm: true }`). 2026-09-27 prod 에서 이 목록을 실제 조회로 부르다가
 *    **DB 커넥션 상한(50)을 넘겼다** — 식은 인스턴스 41개가 한꺼번에 커넥션을 열어, 그 몇 초 동안
 *    워밍과 무관한 화면 조회까지 500(`53300`)이 났다(배포 직후·아침 첫 로그인처럼 전부 식은 때).
 *    서버는 워밍 표시를 보면 로그인만 확인하고 **커넥션을 열기 전에** 멈춘다(`shared.ts requireUser`).
 * ⚠️ 여기 넣는 함수는 **첫 await 가 로그인 게이트**(`requireUser`/`requireRole`…)여야 한다 —
 *    그 전에 DB 를 읽는 함수면 워밍 표시가 안 먹고 커넥션을 연다.
 */
const WARM = [
  // 배차
  'listRecvs', 'listDispts', 'listBids', 'listDisptCarSchs',
  // 기준정보 (탭 7개)
  'listCusts', 'listCars', 'listDrivers', 'listCarAllocs', 'listOwners', 'listGrps', 'listGarages',
  // 정산 — 첫 화면이 같이 부르는 마감 현황(`SettleClose`)이 빠져 있어 늘 식은 채로 떴다(2.6초).
  'listDriveMonths', 'listResultFinish', 'listResultCust', 'listSalaryMonths', 'listResultMisu',
  // 정산 입력단
  'listResultDaily', 'listResultMo', 'listResultMoBoard', 'listResultReq', 'listFinInst', 'listResultMoDef',
  'listResultSalaryStatus', 'listSalaryPublish',
  // 2026-09-27 way 「메뉴 누를 때마다 불러오기」 — 클릭 점검에서 첫 진입 4~5초였던 것들(대시보드·회계·개선요청·사용자)
  'dbHealth', 'listMyFineRequests',
  'listAcctCorps', 'listBankTx', 'listBankAccounts', 'listBankUploads', 'getBankBalances',
  'listCashBook', 'listCashRules', 'listCashCounts', 'listInvoices', 'listDriverOrders', 'getLiquidity', 'listFines',
  'listImprovements', 'countImprovements', 'listAppUsers', 'listNoticesAdmin',
  // 2026-09-30 way 「개선요청처럼 글뿐인 게시판도 불러오기 창이 오래」 — 목록은 데웠는데 **글을 여는 함수**가 빠져
  //    글을 열 때마다 식은 컨테이너를 깨웠다(로그: getImprovement 2.7초 = 부팅). 창·팝업으로 여는 조회를 같이 데운다.
  //    전부 첫 await 가 로그인 게이트라 워밍은 DB 커넥션을 안 연다(issues 134).
  'getImprovement', 'getInvoiceRequest', 'listInvoiceRequests', 'listResultCustDetail',
  'getCommuteMonth', 'commuteMonthOverview', 'listReqTargets', 'listCashBookLog',
  // 2026-09-30 way 「(개선요청 코멘트) 올리는 중… 너무 오래 걸림」 — 로그: commentImprovement 4.3초 중 2.6초가 부팅(웜 0.7~1초).
  //    🔴 쓰기 함수지만 넣어도 된다 — 워밍 표시는 첫 await `requireUser` 에서 DB 전에 멈춘다(위 규칙 「읽기 전용만」은 그 게이트 전 얘기).
  //    넣는 쓰기 함수는 **첫 await 가 requireUser** 인지 꼭 확인할 것(둘 다 `improvementWrite.ts` 첫 줄).
  'commentImprovement', 'createImprovement',
] as const;

function wave(): void {
  void Promise.all(WARM.map((name) => fire(() => warmPing(name))));
}

export function warmUp(): void {
  if (timer) return;
  wave();
  for (const ev of ACTIVITY) window.addEventListener(ev, markActive, { passive: true });
  timer = setInterval(() => {
    // 탭이 숨겨졌거나 자리를 비웠으면 쉰다 — 퇴근 후 열어 둔 탭이 밤새 함수를 깨우면 안 된다.
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - lastActive > IDLE_MS) return;
    wave();
  }, REWARM_MS);
}

/** 로그아웃 시 초기화 — 다음 사람이 로그인하면 다시 깨운다. */
export function resetWarmUp(): void {
  if (timer) clearInterval(timer);
  timer = null;
  for (const ev of ACTIVITY) window.removeEventListener(ev, markActive);
}
